<template>
  <div
    :class="[
      'vc-input',
      `vc-input--size--${size}`,
      {
        'vc-input--readonly': readonly,
        'vc-input--disabled': disabled,
        'vc-input--error': error,
        'vc-input--no-border': noBorder,
        'vc-input--center': center,
        'vc-input--truncate': truncate,
      },
    ]"
    v-bind="attrs"
  >
    <VcLabel v-if="label" :for-id="componentId" :required="required" :error="error">
      {{ label }}
    </VcLabel>

    <div class="vc-input__container">
      <div v-if="$slots.prepend" class="vc-input__decorator">
        <slot name="prepend" :focus-input="focusInput" />
      </div>

      <input
        :id="componentId"
        ref="inputElement"
        v-model="model"
        v-bind="{ ...listeners, ...aria }"
        v-maska="mask"
        :type="inputType"
        :name="name"
        :placeholder="placeholder"
        :readonly="readonly"
        :disabled="disabled"
        :min="minValue"
        :max="maxValue"
        :minlength="minlength"
        :maxlength="maxlength"
        :step="stepValue"
        :autocomplete="computedAutocomplete"
        :aria-label="ariaLabel ?? label"
        :title="browserTooltip === 'enabled' ? message : ''"
        class="vc-input__input"
        :tabindex="tabindex"
        :data-test-id="testIdInput"
        @keydown="keyDown($event)"
        @click.stop="inputClick"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
      />

      <div v-if="clearable && model && !disabled && !readonly" class="vc-input__decorator">
        <VcButton
          :disabled="disabled"
          type="button"
          icon="delete-thin"
          color="neutral"
          variant="no-background"
          class="vc-input__clear text-[--color-body-text] hover:text-[--color-primary-500]"
          :icon-size="size === 'md' ? '1.4rem' : '0.75rem'"
          @keydown.enter.stop.prevent
          @keyup.enter.stop.prevent="clear"
          @click.stop="clear"
        />
      </div>

      <div v-if="type === 'password' && !hidePasswordSwitcher" class="vc-input__decorator">
        <VcButton
          :disabled="disabled"
          :aria-label="$t('ui_kit.buttons.show_hide_password')"
          type="button"
          :icon="passwordVisibilityIcon"
          variant="no-border"
          :icon-size="size === 'md' ? '1.5rem' : '1.25rem'"
          class="vc-input__password-button"
          @click="togglePasswordVisibility"
        />
      </div>

      <div v-if="$slots.append" class="vc-input__decorator">
        <slot name="append" :focus-input="focusInput" />
      </div>
    </div>

    <VcInputDetails :show-empty="showEmptyDetails" :message="message" :error="error" :single-line="singleLineMessage" />
  </div>
</template>

<script setup lang="ts" generic="T extends string | number | null">
import { vMaska } from "maska/vue";
import { provide, computed, ref, useTemplateRef } from "vue";
import { useAttrsOnly, useComponentId, useListeners } from "@/ui-kit/composables";
import type { MaskOptions } from "maska";

export interface IProps {
  modelModifiers?: Record<string, boolean>;
  autocomplete?: string;
  readonly?: boolean;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  ariaLabel?: string;
  label?: string;
  placeholder?: string;
  message?: string;
  singleLineMessage?: boolean;
  error?: boolean;
  noBorder?: boolean;
  hidePasswordSwitcher?: boolean;
  showEmptyDetails?: boolean;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  minlength?: string | number;
  maxlength?: string | number;
  center?: boolean;
  truncate?: boolean;
  type?: "text" | "password" | "number" | "email" | "search" | "tel" | "date"; // Innovadis custom: tel
  size?: VcInputSizeType;
  clearable?: boolean;
  browserTooltip?: "enabled" | "disabled";
  selectOnClick?: boolean;
  testIdInput?: string;
  aria?: Record<string, string | number | null>;
  disableAutocomplete?: boolean;
  tabindex?: string | number;
  mask?: string | MaskOptions;
}

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits<{
  (event: "clear"): void;
  (event: "blur", blurEvent: FocusEvent): void;
  (event: "focus", focusEvent: FocusEvent): void;
}>();

const props = withDefaults(defineProps<IProps>(), {
  type: "text",
  size: "md",
  browserTooltip: "disabled",
  tabindex: 0,
});

const LIMITED_TYPES: IProps["type"][] = ["number", "date"];

const componentId = useComponentId("input");
const listeners = useListeners();
const attrs = useAttrsOnly();

const computedAutocomplete = computed(() => {
  if (props.disableAutocomplete) {
    return "none";
  }

  return props.autocomplete;
});

const inputElement = useTemplateRef("inputElement");
const inputType = computed(() => (props.type === "password" && isPasswordVisible.value ? "text" : props.type));

defineExpose({ inputElement });

const model = defineModel<T>({
  set(value) {
    if (props.disabled) {
      return;
    }

    return !(props.type === "number" && value === "") ? value : undefined;
  },
});

const _size = computed(() => props.size);

const minValue = computed(() => (LIMITED_TYPES.includes(props.type) ? props.min : undefined));
const maxValue = computed(() => (LIMITED_TYPES.includes(props.type) ? props.max : undefined));
const stepValue = computed(() => (props.type === "number" ? props.step : undefined));

const isPasswordVisible = ref<boolean>(false);
const passwordVisibilityIcon = computed<string>(() => (isPasswordVisible.value ? "eye-off" : "eye"));

function togglePasswordVisibility() {
  isPasswordVisible.value = !isPasswordVisible.value;
  focusInput();
}

function focusInput() {
  if (inputElement.value) {
    inputElement.value.focus();
    setTimeout(() => {
      if (inputElement.value?.type !== "date") {
        const len = inputElement.value?.value.length ?? 0;
        inputElement.value?.setSelectionRange(len, len);
      }
    }, 0);
  }
}

function clear() {
  model.value = undefined;
  focusInput();
  emit("clear");
}

// Workaround to fix Safari bug
function keyDown(event: KeyboardEvent) {
  if (props.type === "number") {
    const allowedCharacter = /(^\d*$)|(Backspace|Tab|Delete|ArrowLeft|ArrowRight|ArrowUp|ArrowDown)/;
    if (!allowedCharacter.test(event.key)) {
      event.preventDefault();
    }
  }

  // Innovadis custom
  if (props.type === "tel") {
    // Allow digits, "+", and navigation keys for "tel"
    const allowedCharacter = /(^\d*$)|(Backspace|Tab|Delete|ArrowLeft|ArrowRight|\+)/;
    return !event.key.match(allowedCharacter) && event.preventDefault();
  }
}

function inputClick() {
  if (inputElement.value && props.selectOnClick) {
    inputElement.value.select();
  }
}

provide<VcInputContextType>("inputContext", {
  size: _size,
});
</script>

<style lang="scss">
.vc-input {
  $sizeXs: "";
  $sizeSm: "";
  $sizeMd: "";

  $readonly: "";
  $disabled: "";
  $error: "";
  $noBorder: "";
  $center: "";
  $truncate: "";

  --color: var(--vc-input-base-color, theme("colors.primary.500"));
  --focus-color: rgb(from var(--color) r g b / 0.3);

  --radius: var(--vc-input-radius, var(--vc-radius, 0.5rem));
  --vc-button-radius: calc(var(--radius) - 2px);

  @apply flex flex-col;

  &--size {
    &--xs {
      $sizeXs: &;
    }

    &--sm {
      $sizeSm: &;
    }

    &--md {
      $sizeMd: &;
    }
  }

  &--readonly {
    $readonly: &;
  }

  &--disabled {
    $disabled: &;
  }

  &--error {
    $error: &;

    --color: var(--vc-input-error-color, theme("colors.danger.500"));
  }

  &--no-border {
    $noBorder: &;
  }

  &--center {
    $center: &;
  }

  &--truncate {
    $truncate: &;
  }

  &__container {
    @apply flex items-stretch p-0.5 border border-neutral-400 rounded-[--radius] bg-additional-50 select-none;

    #{$sizeXs} & {
      @apply h-8 text-sm;
    }

    #{$sizeSm} & {
      @apply h-[2.375rem] text-sm;
    }

    #{$sizeMd} & {
      @apply h-11 text-base;
    }

    &:has(input:focus) {
      @apply ring ring-[--focus-color];
    }

    #{$error} & {
      @apply border-[--color] text-[--color];
    }

    #{$disabled} &,
    &:has(input:disabled) {
      @apply bg-neutral-50 cursor-not-allowed;
    }

    #{$noBorder} & {
      @apply border-none;
    }
  }

  &__decorator {
    @apply flex-none flex items-center max-w-[50%] h-full;

    #{$disabled} &,
    &:disabled {
      @apply cursor-not-allowed;
    }
  }

  &__input {
    @apply relative m-px px-2 bg-transparent rounded-[3px] leading-none w-full min-w-0 appearance-none font-normal;

    &::-webkit-search-cancel-button {
      @apply appearance-none;
    }

    &::-webkit-calendar-picker-indicator {
      @apply hidden;
    }

    &::-moz-calendar-picker-indicator {
      @apply hidden;
    }

    &[type="date"] {
      @apply -me-8;

      clip-path: inset(0 2rem 0 0);
    }

    &:autofill {
      &:disabled {
        box-shadow: 0 0 0 1000px #f9fafb inset;
      }

      &:not(:disabled) {
        box-shadow: 0 0 0 1000px #fff inset;
      }
    }

    &:focus {
      @apply outline-none;
    }

    #{$disabled} &,
    &:disabled {
      @apply text-neutral-500 cursor-not-allowed;
    }

    &::placeholder {
      @apply text-neutral-400 font-normal;

      #{$error} & {
        @apply text-danger-400;
      }
    }

    #{$center} & {
      @apply text-center;
    }

    #{$truncate} & {
      @apply truncate;
    }
  }
}
</style>
